import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import gsap from 'gsap';
import { useTranslation } from 'react-i18next';
import { todoAPI, areaAPI } from '../services/api';
import AreaSection from './AreaSection';
import AreaForm from './AreaForm';
import Header from './Header';
import useRiseIn from '../hooks/useRiseIn';
import { prefersReducedMotion } from '../utils/motion';
import styles from './TodoList.module.css';

function StatValue({ value, suffix = '' }) {
  const textRef = useRef(null);
  const displayedRef = useRef(0);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;
    if (prefersReducedMotion()) {
      displayedRef.current = value;
      el.textContent = `${value}${suffix}`;
      return undefined;
    }
    const counter = { v: displayedRef.current };
    const tween = gsap.to(counter, {
      v: value,
      duration: 0.7,
      ease: 'power2.out',
      onUpdate: () => {
        el.textContent = `${Math.round(counter.v)}${suffix}`;
      },
    });
    displayedRef.current = value;
    return () => tween.kill();
  }, [value, suffix]);

  return <Typography ref={textRef} className={styles.statValue} />;
}

function StatsBar({ todos, areas }) {
  const { t } = useTranslation();
  const barRef = useRef(null);
  useRiseIn(barRef);

  const totalOpen = todos.filter((t) => !t.completed).length;
  const totalDone = todos.filter((t) => t.completed).length;
  const totalTasks = todos.length;
  const totalUrgent = todos.filter((t) => t.urgency === 'HIGH' && !t.completed).length;
  const weeklyPct = totalTasks === 0 ? 0 : Math.round((totalDone / totalTasks) * 100);

  const stats = [
    { value: totalOpen, label: t('todos.stats.open') },
    { value: totalDone, label: t('todos.stats.done') },
    { value: areas.length, label: t('todos.stats.areas') },
    { value: weeklyPct, suffix: '%', label: t('todos.stats.complete') },
    { value: totalUrgent, label: t('todos.stats.urgent') },
  ];

  return (
    <Box ref={barRef} className={styles.statsBar}>
      {stats.map((s, i) => (
        <Box key={i} className={styles.statCell}>
          <StatValue value={s.value} suffix={s.suffix} />
          <Typography className={styles.statLabel}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

const TodoList = () => {
  const { t, i18n } = useTranslation();
  const [areas, setAreas] = useState([]);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedAreas, setExpandedAreas] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(false);
      const [areasResponse, todosResponse] = await Promise.all([
        areaAPI.getAll(),
        todoAPI.getAll(),
      ]);
      setAreas(areasResponse.data);
      setTodos(todosResponse.data);
    } catch (err) {
      setError(true);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdate = () => fetchData();
  const handleDelete = () => fetchData();

  const handleToggle = (todoId) => {
    setTodos((prev) =>
      prev.map((t) => (t.id === todoId ? { ...t, completed: !t.completed } : t))
    );
  };

  const getTodosForArea = (areaId) => todos.filter((t) => t.areaId === areaId);

  const isAreaExpanded = (areaId) => expandedAreas[areaId] !== false;

  const toggleAreaExpanded = (areaId) => {
    setExpandedAreas((prev) => ({ ...prev, [areaId]: prev[areaId] === false }));
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString(i18n.language, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  if (loading && areas.length === 0) {
    return (
      <Box className={styles.loadingCenter}>
        <CircularProgress className={styles.loadingProgress} />
      </Box>
    );
  }

  return (
    <Box className={styles.root}>
      <Header dateStr={dateStr} />

      {error && (
        <Alert severity="error" className={styles.errorAlert}>
          {t('todos.loadError')}
        </Alert>
      )}

      {/* Stats */}
      <StatsBar todos={todos} areas={areas} />

      {/* Area Cards */}
      {loading ? (
        <Box className={styles.listLoadingWrap}>
          <CircularProgress className={styles.loadingProgress} />
        </Box>
      ) : (
        <Box className={styles.areaList}>
          {areas.length === 0 ? (
            <Typography className={styles.emptyText}>
              {t('todos.empty')}
            </Typography>
          ) : (
            areas.map((area, i) => (
              <AreaSection
                key={area.id}
                area={area}
                todos={getTodosForArea(area.id)}
                expanded={isAreaExpanded(area.id)}
                onToggleExpanded={() => toggleAreaExpanded(area.id)}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onToggle={handleToggle}
                index={i}
              />
            ))
          )}

          {/* Ghost card - New area */}
          <AreaForm onAreaAdded={handleUpdate} />
        </Box>
      )}
    </Box>
  );
};

export default TodoList;
